import {
  AvatarCurrency,
  ButtonLink,
  ContentIsland,
  Grid,
  Money,
  SectionedGridContainer,
  Box,
  useAnchor,
  IconButton,
  MoreIcon,
  Menu,
  MenuItem,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useVaultQuery } from '../../../api/vaults';
import { useHasRole } from '../../../api/access';
import { MoneyGrow } from '@razz-team/ui-web/src/components/Typography/MoneyGrow';
import { useBoolean } from 'usehooks-ts';
import { useCallback } from 'react';
import { TerminateVaultDialog } from './TerminateVaultDialog';

export function VaultSummary({ vaultId }: { vaultId: string }) {
  const { t } = useTranslation();
  const isAdmin = useHasRole('ADMIN');
  const isPayer = useHasRole('PAYER');
  const vaultQuery = useVaultQuery({ vaultId });
  const anchor = useAnchor<HTMLButtonElement>();
  const terminateFlag = useBoolean();

  const isTerminatable = isAdmin;
  const terminateVault = useCallback(() => {
    anchor.onClose();
    terminateFlag.setTrue();
  }, [anchor, terminateFlag]);

  return (
    <ContentIsland
      title={t('Vault balance')}
      actions={
        isTerminatable ? (
          <IconButton onClick={anchor.onOpen}>
            <MoreIcon />
          </IconButton>
        ) : null
      }
    >
      {isTerminatable ? (
        <Menu
          anchorEl={anchor.element}
          open={anchor.open}
          onClose={anchor.onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={terminateVault}>
            <Typography color="error">{t('Close vault')}</Typography>
          </MenuItem>
        </Menu>
      ) : null}
      <SectionedGridContainer justifyContent="space-between">
        <Grid
          gap={2}
          alignItems="center"
          display="flex"
          size={{
            md: 6,
          }}
        >
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={2}>
              <Money variant="h2" loading={vaultQuery.isPending} value={vaultQuery.data?.balance} />
              {vaultQuery.data ? (
                <AvatarCurrency currency={vaultQuery.data.balance.currency} size="small" />
              ) : null}
            </Box>
            {vaultQuery.data?.totalRewards && vaultQuery.data.totalRewards.amount > 0 && (
              <MoneyGrow
                variant="body2"
                value={vaultQuery.data.totalRewards}
                startAdornment={t('Earned ')}
                endAdornment={t(' all time')}
              />
            )}
          </Box>
        </Grid>
        <Grid
          display="flex"
          gap={1}
          justifyContent="flex-end"
          size={{
            md: 6,
          }}
        >
          {isPayer ? (
            <ButtonLink to="/account/vaults/topup" search={{ vaultId }}>
              {t('Top up')}
            </ButtonLink>
          ) : null}
          {isPayer ? (
            <ButtonLink to="/account/vaults/withdraw" search={{ vaultId }}>
              {t('Withdraw')}
            </ButtonLink>
          ) : null}
        </Grid>
      </SectionedGridContainer>
      {vaultQuery.data && (
        <TerminateVaultDialog
          open={terminateFlag.value}
          onClose={terminateFlag.setFalse}
          vault={vaultQuery.data}
        />
      )}
    </ContentIsland>
  );
}
